import { Link } from 'react-router-dom';
import type { Player } from '@shared/types';
import { isGuestPlayer } from '@/hooks/useSupabaseAuth';
import { SoundControl } from './SoundControl';
import { SettingsMenu } from './SettingsMenu';
import { SignOutButton } from './SignOutButton';

interface NavbarProps {
  player: Player | null;
  onSignOut?: () => void;
  /** Hay un partido en curso (la confirmación de cerrar sesión lo avisa). */
  inMatch?: boolean;
  onSearch?: (q: string) => void;
}

/**
 * Barra superior — equivalente al header de Roblox:
 * Logo a la izquierda, navegación (Discover / Sports / Catalog / Leaderboard),
 * búsqueda global y perfil/login a la derecha.
 *
 * Mantiene una paleta cinematográfica (ink + accent) coherente con el
 * estilo hiperrealista 3D.
 */
export const Navbar = ({ player, onSignOut, inMatch, onSearch }: NavbarProps) => {
  return (
    <header className="sticky top-0 z-40 border-b border-white/5 bg-ink-900/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:gap-6 sm:px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-accent-500 shadow-glow">
            <span className="text-display text-lg font-bold text-ink-900">SA</span>
          </div>
          <span className="text-display hidden text-lg font-bold tracking-wider text-white min-[400px]:inline">
            SportsArena
          </span>
        </Link>

        {/* Navegación principal (como en Roblox: Charts / Marketplace / Create) */}
        <nav className="hidden items-center gap-1 lg:flex">
          {['Discover', 'Sports', 'Catalog', 'Leaderboard'].map((label) => (
            <Link
              key={label}
              to={`/${label.toLowerCase()}`}
              className="rounded-md px-3 py-1.5 text-sm font-semibold text-slate-300 transition hover:bg-white/5 hover:text-white"
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Búsqueda global */}
        <div className="hidden flex-1 lg:block">
          <label className="relative block max-w-md">
            <span className="sr-only">Buscar</span>
            <input
              onChange={(e) => onSearch?.(e.target.value)}
              placeholder="Buscar juegos, estadios, atletas…"
              className="w-full rounded-full border border-white/10 bg-ink-700/60 px-4 py-2 pl-10 text-sm text-slate-200 placeholder-slate-400 outline-none transition focus:border-accent-500 focus:ring-2 focus:ring-accent-500/30"
            />
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </label>
        </div>

        {/* Perfil / Login */}
        <div className="ml-auto flex min-w-0 items-center gap-1.5 sm:gap-3">
          <SoundControl />
          <SettingsMenu />
          {player ? (
            <div className="flex items-center gap-1.5 sm:gap-3">
              <div className="hidden min-w-0 max-w-[10rem] text-right sm:block">
                <div className="text-xs text-slate-400">Conectado como</div>
                <div className="truncate text-sm font-semibold text-white">{player.username}</div>
              </div>
              <div
                className="grid h-9 w-9 place-items-center rounded-full border border-white/10"
                style={{ background: player.avatarConfig.color }}
                title={player.username}
              >
                <span className="text-sm font-bold text-ink-900">
                  {player.avatarConfig.dorsal ?? player.username.slice(0, 2).toUpperCase()}
                </span>
              </div>
              {isGuestPlayer(player) ? (
                <Link to="/login" className="btn-ghost h-9 whitespace-nowrap px-2 text-xs sm:px-3">
                  Iniciar sesión
                </Link>
              ) : (
                onSignOut && <SignOutButton onSignOut={onSignOut} inMatch={inMatch} />
              )}
            </div>
          ) : (
            <Link to="/login" className="btn-primary h-9 px-4 text-sm">
              Iniciar sesión
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

const SearchIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
  </svg>
);